/**
 * detect.ts — locale resolution for the web client.
 *
 * Detection chain (first match wins):
 *   a) /:locale path prefix      — SEO-canonical signal
 *   b) localStorage "ag.locale"  — explicit user choice (switcher)
 *   c) navigator.languages       — browser preference, mapped through
 *                                  the supported list (zh-CN → zh-Hans)
 *   d) "en"
 *
 * The path prefix wins so that a crawled/copy-pasted /hi/… URL always
 * renders Hindi even for an English browser; the storage entry is only
 * written by the language switcher (or when the user lands on a
 * prefixed path) so it records intent, never an accident.
 */
import {
  DEFAULT_LOCALE,
  LOCALE_CODES,
  LOCALE_STORAGE_KEY,
  isSupportedLocale,
  splitLocalePrefix,
} from "./locales";

/** Map a browser language tag onto a supported locale code, or null. */
export function normalizeLanguageTag(tag: string): string | null {
  if (!tag) return null;
  const lower = tag.toLowerCase();
  // The product ships Simplified Chinese only; any zh* preference maps
  // onto zh-Hans rather than falling through to "en".
  if (lower.startsWith("zh")) return "zh-Hans";
  const base = lower.split(/[-_]/)[0];
  return LOCALE_CODES.has(base) ? base : null;
}

/** Read the stored explicit choice, ignoring storage failures (Safari private mode). */
function storedLocale(): string | null {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Navigator preference chain mapped through the supported list. */
function navigatorLocale(): string | null {
  if (typeof navigator === "undefined") return null;
  const candidates: string[] = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ].filter((t): t is string => typeof t === "string" && t.length > 0);
  for (const tag of candidates) {
    const mapped = normalizeLanguageTag(tag);
    if (mapped) return mapped;
  }
  return null;
}

/** Full detection chain. Safe to call during module init and on every route. */
export function detectLocale(): string {
  if (typeof window !== "undefined") {
    // a) path prefix
    const { locale } = splitLocalePrefix(window.location.pathname);
    if (locale) return locale;
    // b) stored explicit choice
    const stored = storedLocale();
    if (stored) return stored;
    // c) browser preference
    const preferred = navigatorLocale();
    if (preferred) return preferred;
  }
  // d) default
  return DEFAULT_LOCALE;
}
